// Geometry of the horizontal bar charts (Bandingkan), shared by the chart
// components and the page planner, which needs the chart's height to know
// whether the comparison table fits under the charts on page 1. Pure: no
// react-pdf, so pagePlan.ts can import it.

export const HBAR_W = 232;
const HBAR_LABEL_W = 100;
export const HBAR_PAD_TOP = 6;
export const HBAR_PAD_BOTTOM = 4;
export const HBAR_PAD_RIGHT = 2;
const HBAR_LABEL_MAX_CHARS_PER_LINE = 25;
const HBAR_LINE_EXTRA = 8;

// Row height from the number of bars: 15pt at ten or more items, up to
// 22pt for a short comparison, so two or six items do not print as a strip
// of hairlines, while six items still leave room for their table under the
// charts on the same page.
export const hbarRowHeight = (rows: number): number =>
  Math.max(15, Math.min(22, Math.round(156 / Math.max(1, rows))));

// A roomy row (20pt and up) draws thicker bars and 7pt labels; the compact
// row keeps the original 8pt bars and 6pt labels.
export const hbarInk = (rowH: number) =>
  rowH >= 20
    ? { barH: 10, subH: 6, subGap: 2, labelSize: 7, valueSize: 7, subValueSize: 6 }
    : { barH: 8, subH: 5, subGap: 1.5, labelSize: 6, valueSize: 6, subValueSize: 5.5 };

export const hbarGeometry = (width: number) => {
  const labelW = Math.round((HBAR_LABEL_W / HBAR_W) * width);
  const maxChars = Math.max(
    1,
    Math.floor(((labelW - 4) / (HBAR_LABEL_W - 4)) * HBAR_LABEL_MAX_CHARS_PER_LINE),
  );
  return { labelW, maxChars };
};

export const wrapHBarLabel = (label: string, maxChars = HBAR_LABEL_MAX_CHARS_PER_LINE): string[] => {
  const words = label.split(" ");
  const lines: string[] = [];
  let current = "";
  let i = 0;
  while (i < words.length && lines.length < 2) {
    const candidate = current ? `${current} ${words[i]}` : words[i];
    if (candidate.length <= maxChars || !current) {
      current = candidate;
      i++;
    } else {
      lines.push(current);
      current = "";
    }
  }
  if (current) lines.push(current);
  const hasMore = i < words.length;
  const lastIdx = lines.length - 1;
  if (hasMore || (lines[lastIdx]?.length ?? 0) > maxChars) {
    const base =
      lines[lastIdx].length > maxChars - 1 ? lines[lastIdx].slice(0, maxChars - 1) : lines[lastIdx];
    lines[lastIdx] = `${base}…`;
  }
  return lines;
};

export const layoutHBarRows = (labels: string[], maxChars = HBAR_LABEL_MAX_CHARS_PER_LINE) => {
  const rowH = hbarRowHeight(labels.length);
  const rowHeights = labels.map((l) =>
    wrapHBarLabel(l, maxChars).length > 1 ? rowH + HBAR_LINE_EXTRA : rowH,
  );
  const rowTops: number[] = [];
  rowHeights.reduce((top, h) => {
    rowTops.push(top);
    return top + h;
  }, HBAR_PAD_TOP);
  const chartH = rowHeights.reduce((sum, h) => sum + h, 0) + HBAR_PAD_TOP + HBAR_PAD_BOTTOM;
  return { rowH, rowHeights, rowTops, chartH };
};

// The SVG height of a horizontal bar chart for these labels at this width.
export const hbarChartHeight = (labels: string[], width: number): number =>
  layoutHBarRows(labels, hbarGeometry(width).maxChars).chartH;
